import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Divider,
  Paper,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  useTheme,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  AutoAwesome as AutoAwesomeIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Favorite as HeartIcon,
  ChatBubbleOutline as CommentIcon,
  Share as ShareIcon,
  BookmarkBorder as BookmarkIcon,
  MoreHoriz as MoreHorizIcon,
  ThumbUpOutlined as ThumbUpIcon,
  Repeat as RetweetIcon,
  BarChart as AnalyticsIcon,
  Send as SendIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  ViewCarousel as CarouselIcon,
  ImageSearchOutlined as SingleImageIcon,
  Twitter as TwitterIcon
} from '@mui/icons-material';
import ImageGalleryDialog from '../ImageGalleryDialog';
import CarouselMediaManager from '../CarouselMediaManager';
import { 
  getPlatformIcon, 
  getPlatformColor, 
  getPlatformLimits,
  getPostTypeLabel,
  getCharacterCountColor
} from '../../utils/platformUtils';

const POST_TYPES = [
  'announcement',
  'problem_solution',
  'features',
  'social_proof',
  'introduction',
  'values',
  'behind_scenes',
  'speaker_highlight'
];

const PLATFORMS = ['twitter', 'facebook', 'instagram', 'linkedin'];

// Check if platform supports carousel
const supportsCarousel = (platform) => {
  return ['instagram', 'tiktok'].includes(platform);
};

// Twitter Preview Component
const TwitterPreview = ({ content, mediaUrl, timestamp = 'now' }) => (
  <Card variant="outlined" sx={{ maxWidth: 400, mx: 'auto' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
        <Avatar
          sx={{ 
            width: 40, 
            height: 40, 
            bgcolor: 'primary.main',
            fontSize: '0.875rem'
          }}
        >
          U
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Your Account
            </Typography>
            <Typography variant="caption" color="text.secondary">
              @yourhandle
            </Typography>
            <Typography variant="caption" color="text.secondary">
              · {timestamp}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
            {content || 'Your tweet content will appear here...'}
          </Typography>
        </Box>
      </Box>
      
      {mediaUrl && (
        <CardMedia
          component="img"
          image={mediaUrl}
          alt="Tweet media"
          sx={{ 
            borderRadius: 1, 
            mb: 1,
            maxHeight: 200,
            objectFit: 'cover'
          }}
        />
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
        <IconButton size="small"><CommentIcon fontSize="small" /></IconButton>
        <IconButton size="small"><RetweetIcon fontSize="small" /></IconButton>
        <IconButton size="small"><HeartIcon fontSize="small" /></IconButton>
        <IconButton size="small"><ShareIcon fontSize="small" /></IconButton>
      </Box>
    </CardContent>
  </Card>
);

// Facebook Preview Component
const FacebookPreview = ({ content, mediaUrl, timestamp = 'Just now' }) => (
  <Card variant="outlined" sx={{ maxWidth: 400, mx: 'auto' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Avatar
          sx={{ 
            width: 40, 
            height: 40, 
            bgcolor: '#1877f2',
            fontSize: '0.875rem'
          }}
        >
          Y
        </Avatar>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Your Page
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {timestamp} · <PublicIcon sx={{ fontSize: 12, ml: 0.5 }} />
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <IconButton size="small">
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      
      <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
        {content || 'Your Facebook post content will appear here...'}
      </Typography>
      
      {mediaUrl && (
        <CardMedia
          component="img"
          image={mediaUrl}
          alt="Facebook post media"
          sx={{ 
            borderRadius: 1, 
            mb: 2,
            maxHeight: 250,
            objectFit: 'cover'
          }}
        />
      )}
      
      <Divider sx={{ mb: 1 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
        <Button size="small" startIcon={<ThumbUpIcon />} color="inherit">
          Like
        </Button>
        <Button size="small" startIcon={<CommentIcon />} color="inherit">
          Comment
        </Button>
        <Button size="small" startIcon={<ShareIcon />} color="inherit">
          Share
        </Button>
      </Box>
    </CardContent>
  </Card>
);

// Instagram Preview Component  
const InstagramPreview = ({ content, mediaUrl, isCarousel = false, carouselItems = [] }) => (
  <Card variant="outlined" sx={{ maxWidth: 400, mx: 'auto' }}>
    <CardContent sx={{ p: 0 }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          sx={{ 
            width: 32, 
            height: 32, 
            bgcolor: '#E4405F',
            fontSize: '0.75rem'
          }}
        >
          Y
        </Avatar>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          youraccount
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <IconButton size="small">
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      
      {/* Media Section */}
      {isCarousel && carouselItems.length > 0 ? (
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            image={carouselItems[0]?.url || '/api/placeholder/400/400'}
            alt="Instagram carousel"
            sx={{ 
              height: 300,
              objectFit: 'cover'
            }}
          />
          {/* Carousel indicator */}
          <Box sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            fontSize: '0.75rem'
          }}>
            <CarouselIcon sx={{ fontSize: 14, mr: 0.5 }} />
            1/{carouselItems.length}
          </Box>
        </Box>
      ) : mediaUrl ? (
        <CardMedia
          component="img"
          image={mediaUrl}
          alt="Instagram post"
          sx={{ 
            height: 300,
            objectFit: 'cover'
          }}
        />
      ) : (
        <Box sx={{
          height: 300,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography color="text.secondary">No media</Typography>
        </Box>
      )}
      
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small"><HeartIcon fontSize="small" /></IconButton>
            <IconButton size="small"><CommentIcon fontSize="small" /></IconButton>
            <IconButton size="small"><SendIcon fontSize="small" /></IconButton>
          </Box>
          <IconButton size="small"><BookmarkIcon fontSize="small" /></IconButton>
        </Box>
        
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          <strong>youraccount</strong> {content || 'Your Instagram caption will appear here...'}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

// LinkedIn Preview Component
const LinkedInPreview = ({ content, mediaUrl, timestamp = 'Just now' }) => (
  <Card variant="outlined" sx={{ maxWidth: 400, mx: 'auto' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Avatar
          sx={{ 
            width: 40, 
            height: 40, 
            bgcolor: '#0077b5',
            fontSize: '0.875rem'
          }}
        >
          Y
        </Avatar>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Your Name
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Your Title · {timestamp}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <IconButton size="small">
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      
      <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
        {content || 'Your LinkedIn post content will appear here...'}
      </Typography>
      
      {mediaUrl && (
        <CardMedia
          component="img"
          image={mediaUrl}
          alt="LinkedIn post media"
          sx={{ 
            borderRadius: 1, 
            mb: 2,
            maxHeight: 250,
            objectFit: 'cover'
          }}
        />
      )}
      
      <Divider sx={{ mb: 1 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
        <Button size="small" startIcon={<ThumbUpIcon />} color="inherit">
          Like
        </Button>
        <Button size="small" startIcon={<CommentIcon />} color="inherit">
          Comment
        </Button>
        <Button size="small" startIcon={<ShareIcon />} color="inherit">
          Share
        </Button>
      </Box>
    </CardContent>
  </Card>
);

const PostEditor = ({ 
  post, 
  onSave, 
  onCancel, 
  onContentChange,
  isCreating,
  campaignData,
  bucketInfo
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    content: post.content || '',
    platform: post.platform || 'twitter',
    postType: post.postType || 'announcement',
    platforms: post.platforms || [post.platform || 'twitter'],
    mediaUrl: post.mediaUrl || '',
    mediaType: post.mediaType || '',
    mediaAltText: post.mediaAltText || '',
    scheduledDate: post.scheduledDate || null,
    scheduledTime: post.scheduledTime || '',
    approved: isCreating ? true : (post.approved || false),
    // New carousel fields
    isCarousel: post.isCarousel || false,
    carouselItems: post.carouselItems || []
  });

  // New state for carousel functionality
  const [mediaType, setMediaType] = useState('single'); // 'single' or 'carousel'
  const [activeTab, setActiveTab] = useState(0);
  
  // Debug logging
  console.log('PostEditor Debug:', {
    isCreating,
    postMediaUrl: post.mediaUrl,
    formDataMediaUrl: formData.mediaUrl,
    isCarousel: formData.isCarousel,
    carouselItems: formData.carouselItems,
    shouldShowCreateButton: !formData.mediaUrl && !formData.isCarousel
  });
  
  const [hasChanges, setHasChanges] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const fileInputRef = useRef(null);

  const platformLimits = getPlatformLimits(formData.platform);
  const characterCount = formData.content.length;
  const isOverLimit = characterCount > platformLimits.maxLength;
  const currentPlatformSupportsCarousel = supportsCarousel(formData.platform);

  useEffect(() => {
    if (onContentChange && hasChanges) {
      onContentChange();
    }
  }, [hasChanges, onContentChange]);

  // Switch to single media if platform doesn't support carousel
  useEffect(() => {
    if (!currentPlatformSupportsCarousel && formData.isCarousel) {
      setFormData(prev => ({
        ...prev,
        isCarousel: false,
        carouselItems: []
      }));
      setMediaType('single');
      setHasChanges(true);
    }
  }, [formData.platform, currentPlatformSupportsCarousel, formData.isCarousel]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleMediaTypeChange = (newType) => {
    setMediaType(newType);
    setFormData(prev => ({
      ...prev,
      isCarousel: newType === 'carousel',
      // Clear opposite media type when switching
      mediaUrl: newType === 'carousel' ? '' : prev.mediaUrl,
      carouselItems: newType === 'single' ? [] : prev.carouselItems
    }));
    setHasChanges(true);
  };

  const handleCarouselChange = (items) => {
    setFormData(prev => ({
      ...prev,
      carouselItems: items
    }));
    setHasChanges(true);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('File size should be less than 5MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    try {
      const url = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        mediaUrl: url,
        mediaType: file.type.split('/')[1],
        mediaFile: file
      }));
      setUploadedFile(file);
      setHasChanges(true);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file');
    }
  };

  const handleRemoveMedia = () => {
    if (formData.mediaUrl && formData.mediaUrl.startsWith('blob:')) {
      URL.revokeObjectURL(formData.mediaUrl);
    }
    setFormData(prev => ({
      ...prev,
      mediaUrl: '',
      mediaType: '',
      mediaFile: null,
      mediaAltText: ''
    }));
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setHasChanges(true);
  };

  const handleSave = () => {
    if (isOverLimit) return;
    
    const extractedHashtags = formData.content.match(/#\w+/g) || [];
    const hashtags = formData.hashtags || extractedHashtags.map(tag => tag.substring(1));
    
    const updateData = {
      content: formData.content,
      platform: formData.platform,
      postType: formData.postType,
      platforms: formData.platforms,
      hashtags: hashtags,
      updatedAt: new Date().toISOString(),
      // Add carousel support
      isCarousel: formData.isCarousel,
      carouselItems: formData.carouselItems
    };

    // Handle single media
    if (formData.mediaUrl && !formData.isCarousel) {
      updateData.mediaUrl = formData.mediaUrl;
      updateData.mediaType = formData.mediaType;
      updateData.mediaAltText = formData.mediaAltText;
      if (formData.mediaFile) {
        updateData.mediaFile = formData.mediaFile;
      }
    }

    if (formData.scheduledDate) {
      updateData.scheduledDate = formData.scheduledDate;
      updateData.scheduledTime = formData.scheduledTime;
    }

    updateData.approved = formData.approved;

    onSave(post.id, updateData);
  };

  const handleSetMediaFromGallery = (mediaData) => {
    setFormData(prev => ({
      ...prev,
      mediaUrl: mediaData.url,
      mediaType: mediaData.type || 'png',
      mediaAltText: ''
    }));
    setHasChanges(true);
    setShowImageGallery(false);
  };

  const handleGenerateContent = async () => {
    setIsGenerating(true);
    try {
      // Mock content generation - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const generatedContent = `🎯 ${bucketInfo?.name || 'Campaign'} insights:\n\n✨ ${campaignData?.businessProfile?.businessName || 'Your business'} is making waves! Here's what you need to know...\n\n#${campaignData?.campaignGoal?.replace('_', '') || 'business'} #socialmedia`;
      
      setFormData(prev => ({ ...prev, content: generatedContent }));
      setHasChanges(true);
    } catch (error) {
      console.error('Content generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        {isCreating ? 'Create New Post' : 'Edit Post'}
        <IconButton onClick={onCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ height: '70vh' }}>
          <Grid item xs={12} md={8} sx={{ borderRight: { md: 1 }, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
            {/* Content Editor */}
            <Paper variant="outlined" sx={{ p: 2, m: 2, mb: 1, flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  Post Content
                </Typography>
                <Button
                  size="small"
                  startIcon={isGenerating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                  onClick={handleGenerateContent}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Generating...' : 'Generate Content'}
                </Button>
              </Box>
              
              <TextField
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                placeholder={`Write your ${formData.platform} post...`}
                value={formData.content}
                onChange={handleInputChange('content')}
                error={isOverLimit}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography 
                  variant="caption" 
                  color={getCharacterCountColor(characterCount, platformLimits.maxLength)}
                >
                  {characterCount}/{platformLimits.maxLength} characters
                </Typography>
                {isOverLimit && (
                  <Typography variant="caption" color="error">
                    Exceeds character limit
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* Media Section */}
            <Paper variant="outlined" sx={{ p: 2, m: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  Media
                </Typography>
                
                {/* Media Type Selector for supported platforms */}
                {currentPlatformSupportsCarousel && (
                  <Tabs
                    value={mediaType}
                    onChange={(e, newValue) => handleMediaTypeChange(newValue)}
                    size="small"
                  >
                    <Tab 
                      value="single" 
                      label="Single" 
                      icon={<SingleImageIcon />} 
                      iconPosition="start"
                      sx={{ minHeight: 'auto', py: 1 }}
                    />
                    <Tab 
                      value="carousel" 
                      label="Carousel" 
                      icon={<CarouselIcon />} 
                      iconPosition="start"
                      sx={{ minHeight: 'auto', py: 1 }}
                    />
                  </Tabs>
                )}
              </Box>

              {/* Carousel Media Manager */}
              {formData.isCarousel && currentPlatformSupportsCarousel ? (
                <CarouselMediaManager
                  platform={formData.platform}
                  initialItems={formData.carouselItems}
                  onChange={handleCarouselChange}
                  disabled={false}
                  maxHeight={400}
                  compact={true}
                />
              ) : (
                // Single Media Section
                <>
                  {/* Debug info */}
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Debug: mediaUrl = "{formData.mediaUrl}", showButtons = {!formData.mediaUrl ? 'true' : 'false'}
                  </Typography>
                  
                  {formData.mediaUrl ? (
                    <Card sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={formData.mediaUrl}
                        alt={formData.mediaAltText || 'Post media'}
                        sx={{ objectFit: 'contain', bgcolor: 'grey.100' }}
                      />
                      <CardActions sx={{ justifyContent: 'space-between' }}>
                        <TextField
                          size="small"
                          label="Alt text"
                          value={formData.mediaAltText}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, mediaAltText: e.target.value }));
                            setHasChanges(true);
                          }}
                          placeholder="Describe this image"
                          fullWidth
                          sx={{ mr: 1 }}
                        />
                        <Tooltip title="Remove media">
                          <IconButton 
                            onClick={handleRemoveMedia}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      flexDirection: { xs: 'column', sm: 'row' },
                      mt: 2 
                    }}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                        id="media-upload"
                      />
                      <label htmlFor="media-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          fullWidth
                        >
                          Upload Image
                        </Button>
                      </label>
                      
                      <Button
                        variant="outlined"
                        startIcon={<PhotoLibraryIcon />}
                        onClick={() => setShowImageGallery(true)}
                        fullWidth
                        sx={{ 
                          backgroundColor: 'primary.light',
                          color: 'primary.contrastText',
                          fontWeight: 'bold',
                          '&:hover': {
                            backgroundColor: 'primary.main'
                          }
                        }}
                      >
                        Create Media
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Paper>

            {/* Preview Section */}
            <Paper variant="outlined" sx={{ p: 2, m: 2, mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {platformLimits.name} Preview
              </Typography>
              <Box sx={{ mt: 2 }}>
                {formData.platform === 'twitter' && (
                  <TwitterPreview content={formData.content} mediaUrl={formData.mediaUrl} />
                )}
                {formData.platform === 'facebook' && (
                  <FacebookPreview content={formData.content} mediaUrl={formData.mediaUrl} />
                )}
                {formData.platform === 'instagram' && (
                  <InstagramPreview 
                    content={formData.content} 
                    mediaUrl={formData.mediaUrl}
                    isCarousel={formData.isCarousel}
                    carouselItems={formData.carouselItems}
                  />
                )}
                {formData.platform === 'linkedin' && (
                  <LinkedInPreview content={formData.content} mediaUrl={formData.mediaUrl} />
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Primary Platform
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.platform}
                    onChange={handleInputChange('platform')}
                  >
                    {formData.platforms.map((platform) => (
                      <MenuItem key={platform} value={platform}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            sx={{
                              bgcolor: getPlatformColor(theme, platform),
                              width: 20,
                              height: 20,
                              '& .MuiSvgIcon-root': { fontSize: 12 }
                            }}
                          >
                            {React.createElement(getPlatformIcon(platform) || TwitterIcon, { fontSize: 'small' })}
                          </Avatar>
                          {platform.charAt(0).toUpperCase() + platform.slice(1)}
                          {supportsCarousel(platform) && (
                            <Chip size="small" label="Carousel" color="primary" variant="outlined" />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Carousel support notice */}
                {currentPlatformSupportsCarousel && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                    ✨ This platform supports carousel posts
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Post Type
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.postType}
                    onChange={handleInputChange('postType')}
                  >
                    {POST_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {getPostTypeLabel(type)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Schedule Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon fontSize="small" />
                  Schedule
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Date"
                    value={formData.scheduledDate ? new Date(formData.scheduledDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const newDate = e.target.value ? new Date(e.target.value) : null;
                      setFormData(prev => ({ ...prev, scheduledDate: newDate }));
                      setHasChanges(true);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                
                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    type="time"
                    label="Time"
                    value={formData.scheduledTime || ''}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, scheduledTime: e.target.value }));
                      setHasChanges(true);
                    }}
                    InputLabelProps={{ shrink: true }}
                    helperText={!formData.scheduledDate && "Select a date first"}
                    disabled={!formData.scheduledDate}
                  />
                </Box>
              </Box>

              {/* Approval Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {formData.approved ? <CheckCircleIcon fontSize="small" color="success" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                  Approval Status
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.approved}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, approved: e.target.checked }));
                        setHasChanges(true);
                      }}
                      color="success"
                    />
                  }
                  label={formData.approved ? 'Approved' : 'Needs Review'}
                />
              </Box>

              {/* Media Stats */}
              {(formData.mediaUrl || formData.carouselItems.length > 0) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Media Info
                  </Typography>
                  {formData.isCarousel ? (
                    <Typography variant="body2" color="text.secondary">
                      Carousel: {formData.carouselItems.length} items
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Single media attached
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={isOverLimit || (!formData.content.trim())}
        >
          {isCreating ? 'Create Post' : 'Save Changes'}
        </Button>
      </DialogActions>

      <ImageGalleryDialog
        open={showImageGallery}
        onClose={() => setShowImageGallery(false)}
        setPostImg={handleSetMediaFromGallery}
        postImg={{ url: formData.mediaUrl, type: formData.mediaType }}
      />
    </>
  );
};

export default PostEditor; 